// src/pages/work-order-management-system/index.jsx
import React, { useCallback, useState } from 'react';
import Icon from '../../components/appIcon';
import Button from '../../components/ui/Button';
import { useWorkOrderAPI } from '../../hooks/useWorkOrderAPI';
import NewWorkOrderModal from './components/NewWorkOrderModal';
import BudgetModal from './components/BudgetModal';
import PdfLibraryModal from './components/PdfLibraryModal';

const WorkOrderManagementSystem = () => {
    const { inventory, createWorkOrder, isLoading, error, refreshData } = useWorkOrderAPI();

    const [isNewReportOpen, setIsNewReportOpen] = useState(false);
    const [isWorkOrderModalOpen, setIsWorkOrderModalOpen] = useState(false);
    const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
    const [isPdfLibraryOpen, setIsPdfLibraryOpen] = useState(false);

    // ✅ Estados para edición desde historial
    const [editingWorkOrderRaw, setEditingWorkOrderRaw] = useState(null); // work-order (BudgetModal)
    const [editingServiceReportRaw, setEditingServiceReportRaw] = useState(null); // service-report (NewWorkOrderModal)

    // Cuando se crea un nuevo reporte desde NewWorkOrderModal
    const handleCreateReport = async (data) => {
        const newOrder = await createWorkOrder(data);
        setSelectedWorkOrder(newOrder || null);
    };

    // ✅ Editar desde historial: ahora recibe { type, rawData }
    const handleEditFromHistory = useCallback((payload) => {
        if (!payload) return;

        const { type, rawData } = payload;
        if (!rawData) return;

        // (Opcional) cerrar librería aquí también por si acaso
        setIsPdfLibraryOpen(false);

        if (type === 'work-order') {
            setEditingWorkOrderRaw(rawData);
            setIsWorkOrderModalOpen(true);
            return;
        }

        if (type === 'service-report') {
            setEditingServiceReportRaw(rawData);
            setIsNewReportOpen(true);
            return;
        }

        console.warn('[WorkOrderManagementSystem] Tipo no soportado para editar:', type);
    }, []);

    const closeNewReportModal = useCallback(() => {
        setIsNewReportOpen(false);
        setEditingServiceReportRaw(null);
    }, []);

    const closeBudgetModal = useCallback(() => {
        setIsWorkOrderModalOpen(false);
        setEditingWorkOrderRaw(null);
    }, []);

    return (
        <div className="min-h-screen bg-slate-950 text-gray-100">
            <div className="max-w-4xl mx-auto px-4 py-10 space-y-8">
                {/* HEADER SIMPLE */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <span className="h-1.5 w-6 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500" />
                            Taller · Módulo de Órdenes
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="rounded-2xl bg-gradient-to-br from-cyan-500/40 via-blue-500/30 to-indigo-600/40 p-3 border border-white/10 shadow-md shadow-black/40">
                                <Icon name="Wrench" size={26} className="text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                                    Panel de Órdenes
                                </h1>
                                <p className="text-gray-400 text-sm sm:text-base">
                                    Crea órdenes de trabajo, reportes técnicos y gestiona tus PDFs.
                                </p>
                            </div>
                        </div>
                        {error && (
                            <div className="flex items-center gap-2 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 px-3 py-2 rounded-xl w-fit">
                                <Icon name="AlertTriangle" size={14} />
                                {error}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <Button
                            variant="outline"
                            className="border-white/20 text-white hover:border-cyan-400/50 hover:text-cyan-50"
                            onClick={refreshData}
                            disabled={isLoading}
                            type="button"
                        >
                            <Icon name="RefreshCcw" size={16} className="mr-2" />
                            Sincronizar
                        </Button>
                    </div>
                </div>

                {/* TRES BOTONES PRINCIPALES */}
                <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-3">
                    {/* 1. ORDEN DE TRABAJO (BudgetModal) */}
                    <button
                        type="button"
                        onClick={() => {
                            setSelectedWorkOrder(null);
                            setEditingWorkOrderRaw(null); // ✅ nuevo en blanco
                            setIsWorkOrderModalOpen(true);
                        }}
                        className="flex flex-col items-start justify-between h-full bg-emerald-900/40 border border-emerald-500/50 text-left rounded-2xl px-4 py-5 hover:bg-emerald-800/60 transition-colors"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className="rounded-xl bg-emerald-500/60 p-2.5">
                                <Icon name="ClipboardList" size={20} className="text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-emerald-100/80">Orden de trabajo</p>
                                <h2 className="text-base font-semibold text-white">Crear nueva orden</h2>
                            </div>
                        </div>
                        <p className="text-xs text-emerald-50/80">
                            Refacciones + servicios, totales e impresión de la orden en PDF.
                        </p>
                    </button>

                    {/* 2. REPORTE TÉCNICO (NewWorkOrderModal) */}
                    <button
                        type="button"
                        onClick={() => {
                            setEditingServiceReportRaw(null); // ✅ nuevo en blanco
                            setIsNewReportOpen(true);
                        }}
                        className="flex flex-col items-start justify-between h-full bg-cyan-900/40 border border-cyan-500/50 text-left rounded-2xl px-4 py-5 hover:bg-cyan-800/60 transition-colors"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className="rounded-xl bg-cyan-500/60 p-2.5">
                                <Icon name="FilePlus" size={20} className="text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-cyan-100/80">Reporte técnico</p>
                                <h2 className="text-base font-semibold text-white">Nuevo reporte de trabajo</h2>
                            </div>
                        </div>
                        <p className="text-xs text-cyan-50/80">
                            Datos del vehículo, diagnóstico técnico y reporte profesional en PDF.
                        </p>
                    </button>

                    {/* 3. HISTORIAL DE PDFS (PdfLibraryModal) */}
                    <button
                        type="button"
                        onClick={() => setIsPdfLibraryOpen(true)}
                        className="flex flex-col items-start justify-between h-full bg-amber-900/40 border border-amber-500/50 text-left rounded-2xl px-4 py-5 hover:bg-amber-800/60 transition-colors"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className="rounded-xl bg-amber-500/70 p-2.5">
                                <Icon name="FolderOpen" size={20} className="text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-amber-100/80">Historial</p>
                                <h2 className="text-base font-semibold text-white">Archivos PDF</h2>
                            </div>
                        </div>
                        <p className="text-xs text-amber-50/80">
                            Consulta y filtra todos tus PDFs guardados por fecha, mes y cliente.
                        </p>
                    </button>
                </div>

                {isLoading && <p className="text-xs text-gray-400">Cargando datos de inventario / órdenes...</p>}
            </div>

            {/* MODAL: REPORTE DE TRABAJO */}
            {isNewReportOpen && (
                <NewWorkOrderModal
                    isOpen={isNewReportOpen}
                    onClose={closeNewReportModal}
                    onCreate={handleCreateReport}
                    userRole="admin"
                    currentUser={{ name: 'Sistema' }}
                    inventory={inventory}
                    vehicleHistory={[]}
                    initialData={editingServiceReportRaw} // ✅ Editar desde historial
                />
            )}

            {/* MODAL: ORDEN DE TRABAJO */}
            {isWorkOrderModalOpen && (
                <BudgetModal
                    isOpen={isWorkOrderModalOpen}
                    onClose={closeBudgetModal}
                    selectedWorkOrder={selectedWorkOrder}
                    inventory={inventory}
                    workOrders={[]}
                    initialData={editingWorkOrderRaw} // ✅ Editar desde historial
                />
            )}

            {/* MODAL: HISTORIAL DE PDFS */}
            {isPdfLibraryOpen && (
                <PdfLibraryModal
                    isOpen={isPdfLibraryOpen}
                    onClose={() => setIsPdfLibraryOpen(false)}
                    onEdit={handleEditFromHistory} // ✅ recibe { type, rawData }
                />
            )}
        </div>
    );
};

export default WorkOrderManagementSystem;
